import { useState } from "react";
import { useTrading } from "@helm/core";
import { ConfirmDialog } from "../components/ConfirmDialog";
import { forceExit } from "./actions";

/**
 * Persistent red banner while the gateway holds back part of an exit (ADR-0050): a protective
 * order's cancel was not confirmed by Rithmic within the wait, so sending that part could reverse
 * the account if the order also fills. Driven by the gateway's Error `FLATTEN_WAITING` notice,
 * cleared by `FLATTEN_WAITING_CLEARED` (a READY `Snapshot` replaces the set). The only way to
 * send the held part is "Force exit anyway", behind a confirmation that says it can reverse.
 * Not dismissible. Desktop and mobile shells.
 */
export function FlattenWaitingBanner(): React.JSX.Element | null {
  const held = useTrading((s) => s.heldExits);
  const [asking, setAsking] = useState<string | null>(null);
  const [busy, setBusy] = useState(false);
  if (held.size === 0) return null;
  return (
    <div className="banner banner-danger unconfirmed-banner" role="alert" data-testid="flatten-waiting">
      <strong>Flatten waiting — part of the exit is held back</strong>
      {[...held].map(([account, text]) => (
        <span key={account} className="unconfirmed-why">
          {text}{" "}
          <button
            type="button"
            className="btn btn-danger"
            data-testid="force-exit"
            onClick={() => {
              setAsking(account);
            }}
          >
            Force exit anyway…
          </button>
        </span>
      ))}
      {asking !== null && (
        <ConfirmDialog
          title={`Force exit ${asking}?`}
          confirmLabel="Force exit — may reverse"
          busy={busy}
          onCancel={() => {
            setAsking(null);
          }}
          onConfirm={() => {
            setBusy(true);
            void forceExit(asking).finally(() => {
              setBusy(false);
              setAsking(null);
            });
          }}
        >
          <p>
            Rithmic has not confirmed cancelling the protective stop. Forcing sends a market exit for the held
            contracts now: if that stop also fills, your position REVERSES (you end up in the opposite
            direction, unprotected).
          </p>
          <p>
            Safer: check the stop in R|Trader first. TradePrizm sends the exit by itself the moment the cancel
            is confirmed.
          </p>
        </ConfirmDialog>
      )}
    </div>
  );
}
